import React, { useState } from "react";
import { Search, Car, Save, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useNavigate } from "react-router-dom";

const VINSearch = ({ onVehicleFound, compact = false }) => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [vin, setVin] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleVINLookup = async (e) => {
    e.preventDefault();
    setError("");
    setVehicle(null);
    
    const cleanVin = vin.toUpperCase().trim();
    
    // Validate VIN
    if (cleanVin.length !== 17) {
      setError("VIN must be exactly 17 characters");
      return;
    }
    
    if (/[IOQ]/.test(cleanVin)) {
      setError("VIN cannot contain letters I, O, or Q");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/vin/decode`, { vin: cleanVin });
      const data = response.data;
      
      if (!data.year || !data.make || !data.model) {
        setError("Could not decode VIN. Please verify it's correct.");
        return;
      }
      
      setVehicle(data);
      
      // Callback for parent component
      if (onVehicleFound) {
        onVehicleFound({
          year: data.year,
          make: data.make,
          model: data.model,
          engine: data.engine
        });
      }
      
      toast.success(`Found: ${data.year} ${data.make} ${data.model}`);
    } catch (err) {
      console.error("VIN lookup error:", err);
      setError(err.response?.data?.detail || "Failed to decode VIN. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVehicle = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to save vehicles");
      navigate("/auth");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/garage/vehicles`,
        null,
        {
          params: { vin: vehicle.vin },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Vehicle saved to your garage!");
    } catch (err) {
      console.error("Save vehicle error:", err);
      if (err.response?.data?.detail === "Vehicle already saved") {
        toast.info("This vehicle is already in your garage");
      } else {
        toast.error(err.response?.data?.detail || "Failed to save vehicle");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFindParts = () => {
    if (vehicle) {
      navigate(`/shop?year=${vehicle.year}&make=${encodeURIComponent(vehicle.make)}&model=${encodeURIComponent(vehicle.model)}`);
    }
  };

  if (compact) {
    return (
      <form onSubmit={handleVINLookup} className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder="Enter 17-digit VIN"
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase())}
            maxLength={17}
            className="bg-white border-gray-300 text-gray-900 uppercase tracking-wider pr-10"
            data-testid="vin-input-compact"
          />
          {vehicle && (
            <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
          )}
        </div>
        <Button 
          type="submit" 
          disabled={loading || vin.length !== 17}
          className="bg-primary hover:bg-primary/90"
          data-testid="vin-search-btn-compact"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </form>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="vin-search-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Car className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-gray-900 uppercase">VIN Lookup</h3>
          <p className="text-gray-500 text-sm">Enter your VIN to find compatible parts instantly</p>
        </div>
      </div>
      
      <form onSubmit={handleVINLookup} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Vehicle Identification Number (VIN)
          </label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Enter 17-character VIN (e.g., 1HGCM82633A004352)"
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              maxLength={17}
              className="flex-1 bg-white border-gray-300 text-gray-900 uppercase tracking-wider font-mono"
              data-testid="vin-input"
            />
            <Button 
              type="submit" 
              disabled={loading || vin.length !== 17}
              className="bg-primary hover:bg-primary/90 px-6"
              data-testid="vin-search-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Decoding...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Decode VIN
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Find your VIN on the driver's side dashboard or door jamb sticker
          </p>
        </div>
        
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
        
        {vehicle && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="vin-result">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Vehicle Found!</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h4>
                {vehicle.engine && (
                  <p className="text-gray-600 mt-1">Engine: {vehicle.engine}</p>
                )}
                {vehicle.body_type && (
                  <p className="text-gray-500 text-sm">Body: {vehicle.body_type}</p>
                )}
                <p className="text-gray-400 text-xs mt-2 font-mono">VIN: {vehicle.vin}</p>
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={handleFindParts}
                className="bg-primary hover:bg-primary/90"
                data-testid="find-parts-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Find Compatible Parts
              </Button>
              <Button 
                variant="outline"
                onClick={handleSaveVehicle}
                disabled={saving}
                className="border-gray-300"
                data-testid="save-vehicle-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save to My Garage
              </Button>
            </div>
          </div>
        )}
      </form>
      
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          <strong>Where to find your VIN:</strong> Driver's side dashboard (visible through windshield), 
          driver's door jamb sticker, vehicle registration, or insurance documents.
        </p>
      </div>
    </div>
  );
};

export default VINSearch;
